import C from "../../../Constants";
// マスターデータのサービス
import {
  // 船舶情報マスター
  EnterpriseManningVesselsService,
  // 法人勤務種別サービス
  EnterpriseManningStatusService,
  EnterpriseCrewsService,
} from "../../../services/master";
/**
 * イベントレンダラー
 * @param {String} displayMode - 表示モード
 * - VESSEL: 船舶単位
 * - CREWS: 人単位
 * @example
 * // 船舶単位表示のイベントレンダラーを作成
 * const eventRenderer = EventRenderer(C.DISPLAY_MODE.VESSEL).build();
 * // スケジューラのイベントレンダラーとして使用
 * scheduler.eventRenderer = eventRenderer;
 */
export default displayMode => {
  // 乗船中イベントの色リスト
  const VESSEL_EVENT_COLOR_LIST = ["#028AC7", "#0EA5E9"];

  // 休暇希望のイベント色
  // 高優先度は赤色、中・低優先度はオレンジ
  const DAY_OFF_REQUEST_EVENT_COLOR = {
    [C.DAY_OFF_REQUEST_PRIORITY.HIGH]: "#F23B2F",
    [C.DAY_OFF_REQUEST_PRIORITY.MEDIUM]: "#FB923C",
    [C.DAY_OFF_REQUEST_PRIORITY.LOW]: "#FB923C",
  };

  // アラート種別ごとのアイコン色（描画順）
  const ALERT_ICON_TYPES = [
    {
      type: C.EVENT_ALERT_TYPE.CAUTION,
      color: C.EVENT_ALERT_INCON_COLORS.CAUTION,
    },
    {
      type: C.EVENT_ALERT_TYPE.WARNING,
      color: C.EVENT_ALERT_INCON_COLORS.WARNING,
    },
  ];

  /**
   * 乗船中（VESSEL）イベントの色・名前・スタイルを設定
   */
  const _renderVesselEvent = (eventRecord, renderData) => {
    let eventName = "";

    if (displayMode === C.DISPLAY_MODE.VESSEL) {
      // 船舶単位表示：resource内の出現順でカラーリストを循環
      const eventStore = eventRecord.eventStore;
      const records = eventStore.query(
        record =>
          record.scheduling_status === eventRecord.scheduling_status &&
          eventRecord.resourceId === record.resourceId,
        true
      );
      const idx = records.findIndex(record => record.id === eventRecord.id);
      renderData.eventColor =
        VESSEL_EVENT_COLOR_LIST[idx % VESSEL_EVENT_COLOR_LIST.length];

      eventName = _findEventNameByVesselMode(eventRecord);
    } else if (displayMode === C.DISPLAY_MODE.CREWS) {
      // 人単位表示：船舶マスタのcolorを使用
      const vessel = EnterpriseManningVesselsService.findById(
        eventRecord.enterprise_manning_vessel_id
      );
      renderData.eventColor =
        (vessel && vessel.color) || VESSEL_EVENT_COLOR_LIST[0];

      eventName = _findEventNameByCrewMode(eventRecord);
    }

    // 予定、計画中でスタイルを変更
    // 計画中は透過する。
    renderData.eventStyle =
      eventRecord.scheduling_status === C.SCHEDULING_STATUS.SCHEDULED
        ? "plain"
        : "colored";

    return { eventName, textColorStyle: "" };
  };

  /**
   * 下船中（ASHORE）イベントの色・名前を設定
   * 下船中状態はグレーで表示
   */
  const _renderAshoreEvent = (eventRecord, renderData) => {
    renderData.eventColor = "#bababa";
    const status = EnterpriseManningStatusService.findById(
      eventRecord.enterprise_manning_status_id
    );
    return { eventName: status ? status.name : "", textColorStyle: "" };
  };

  /**
   * 休暇希望（DAY_OFF_REQUEST）イベントの色・名前・スタイルを設定
   * 赤色／オレンジの線で表示
   */
  const _renderDayOffRequestEvent = (eventRecord, renderData) => {
    renderData.eventStyle = "line";
    const priority = eventRecord.priority;
    const eventColor = DAY_OFF_REQUEST_EVENT_COLOR[priority];

    renderData.eventColor = `${eventColor}:`;

    return {
      eventName: "休暇希望・" + C.DAY_OFF_REQUEST_PRIORITY_NAME[priority],
      textColorStyle: `color: ${eventColor};`,
    };
  };

  /**
   * 注意・警戒アラートアイコンタグを生成
   */
  const _buildAlertIcons = alerts => {
    return ALERT_ICON_TYPES.filter(({ type }) =>
      alerts.some(alert => alert.alert_type === type)
    ).map(({ color }) => ({
      tag: "i",
      class:
        "vs-icon notranslate icon-scale material-icons small bg-small vs-icon-bg",
      text: "error",
      style: `margin-left: 0.2rem; color: ${color};`,
    }));
  };

  const build = () => {
    return ({ eventRecord, renderData }) => {
      let eventName = "";
      let textColorStyle = "";

      if (eventRecord.event_type === C.SCHEDULING_EVENT_TYPE.VESSEL) {
        ({ eventName, textColorStyle } = _renderVesselEvent(
          eventRecord,
          renderData
        ));
      } else if (eventRecord.event_type === C.SCHEDULING_EVENT_TYPE.ASHORE) {
        ({ eventName, textColorStyle } = _renderAshoreEvent(
          eventRecord,
          renderData
        ));
      } else if (
        eventRecord.event_type === C.SCHEDULING_EVENT_TYPE.DAY_OFF_REQUEST
      ) {
        ({ eventName, textColorStyle } = _renderDayOffRequestEvent(
          eventRecord,
          renderData
        ));
      }

      renderData.style = "text-align: center;" + textColorStyle;

      // イベントに埋め込むタグ
      return [
        { tag: "span", text: eventName },
        ..._buildAlertIcons(eventRecord.alerts),
      ];
    };
  };

  /**
   * 船単位表示の時のイベント名を取得
   * @param {*} eventRecord
   * @returns
   */
  const _findEventNameByVesselMode = eventRecord => {
    const crew = EnterpriseCrewsService.findById(eventRecord.crew_id);
    return crew ? crew.name : "";
  };

  /**
   * 人単位表示の時のイベント名を取得
   * @param {*} eventRecord
   * @returns
   */
  const _findEventNameByCrewMode = eventRecord => {
    const vessel = EnterpriseManningVesselsService.findById(
      eventRecord.enterprise_manning_vessel_id
    );
    const rolePosition = EnterpriseManningVesselsService.findRolePositionById(
      eventRecord.enterprise_manning_vessel_role_position_id
    );

    const vesselName = vessel ? vessel.name : "";
    const roleName = rolePosition ? rolePosition.name : "";

    return `${vesselName}・${roleName}`;
  };

  return {
    build,
  };
};
